import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { Checkbox, Icon, Loader } from "semantic-ui-react";
import { connect, type ConnectedProps } from "react-redux";
import { noop } from "lodash";

import Tooltip from "../../../../components/common/tooltip/Tooltip";
import { threatDefenseDirectSendTooltip } from "../../constants";
import ValidatedForm from "../../../../components/forms/ValidatedForm";
import { getEmailSettingsSchema, getVerifyEmailSchema } from "utils/validationSchemas/emailSettingsSchema";
import FormFooter from "components/forms/FormFooter";
import { type EmailSetting, type VerifyEmailModel } from "../../types";
import { VerifyEmail } from "../../EmailSettings/VerifyEmail/VerifyEmail";

import type { AppDispatch, RootState } from "../../../Application/globaltypes/redux";
import { threatDefenceSettingsSelector, threatDefenceSmtpSettingsSelector } from "./state/selectors";
import {
  deleteThreatDefenceSettings,
  fetchThreatDefenceSettings,
  upsertThreatDefenceSettings,
} from "./state/thunks/threatDefenceSettingsThunk";
import {
  deleteThreatDefenceSmtpSettings,
  fetchThreatDefenceSmtpSettings,
  upsertThreatDefenceSmtpSettings,
  verifyThreatDefenceSmtpSettings,
} from "./state/thunks/threatDefenceSmtpSettingsThunk";
import { bindAction } from "interfaces";
import { resetVerify } from "./state/slices/threatDefenceSmtpSettingsSlice";

import { RolePermissions } from "../../../../enums";
import { permissionsToAccountSelector } from "../state/slices/permissionsToAccountSlice";
import { Typography } from "../../../../components/common/Typography/Typography";

import styles from "./ThreatDefense.module.scss";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../featureFlags";

import { SmtpConfigurationForm } from "./SmtpConfigurationForm";
import { MsGraphSettings, AssignedDomainSection, WhitelistDomainSection } from "./ThreatDefenseSections";

interface ThreatDefenseProps {
  accountId: number;
}
export type ThreatDefenseComponentProps = ThreatDefenseProps & PropsFromRedux;

export const ThreatDefense = (props: ThreatDefenseComponentProps) => {
  const isThreatDefenceDomainEnabled = useFeatureFlag(FeatureFlags.ThreatDefenceDomains);
  const isSmtpDirectSendEnabled = useFeatureFlag(FeatureFlags.ThreatDefenceCustomSmtp);
  const {
    getThreatDefenceSettings,
    getSmtpSettings,
    saveSmtpSettings,
    deleteSmtpSettings,
    verifySmtpSettings,
    resetVerifyState,
  } = props;

  const canEdit = useMemo(
    () => props.accountOnEditPermissions.includes(RolePermissions.AccountsSettingsManage),
    [props.accountOnEditPermissions],
  );

  const [isSmtpEnabled, setSmtpEnabled] = useState(false);
  const [credentialsRequired, setCredentialsRequired] = useState(false);

  useEffect(() => {
    if (!isSmtpDirectSendEnabled) return;

    getSmtpSettings(props.accountId);
  }, [getSmtpSettings, isSmtpDirectSendEnabled, props.accountId]);

  const saveEmailSettings = async (settings: EmailSetting) => {
    await saveSmtpSettings(props.accountId, settings);
  };

  const deleteEmailSettings = async () => {
    await deleteSmtpSettings(props.accountId);
  };

  const verifyEmailSettings = async (email: string, settings: EmailSetting) => {
    return await verifySmtpSettings(email, settings);
  };

  const entityExists = !!props.smtpSettings?.id;

  useEffect(() => {
    setSmtpEnabled(!!props.smtpSettings?.id);
    setCredentialsRequired(
      (!!props.smtpSettings?.userName && !props.smtpSettings?.id) || !!props.smtpSettings?.password,
    );
    if (!props.smtpSettings?.id) {
      resetVerifyState();
    }
  }, [props.smtpSettings, resetVerifyState]);

  const schema = useMemo(() => {
    return getEmailSettingsSchema(isSmtpEnabled, credentialsRequired);
  }, [isSmtpEnabled, credentialsRequired]);

  const formik = useFormik<Partial<EmailSetting>>({
    initialValues: props.smtpSettings ?? defaultSettings,
    onSubmit: async (values: Partial<EmailSetting>) => {
      if (isSmtpEnabled) {
        const casted = schema.cast(values) as EmailSetting;
        casted.password = casted.password || undefined;
        casted.userName = casted.userName || undefined;
        await saveEmailSettings(casted);
      } else if (props.smtpSettings?.id) {
        await deleteEmailSettings();
      }
    },
    validationSchema: schema,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const { userName, password } = formik.values;

  const verifyEmailSchema = useMemo(() => getVerifyEmailSchema(), []);
  const verifyFormik = useFormik<Partial<VerifyEmailModel>>({
    initialValues: defaultVerifyEmail,
    onSubmit: noop,
    validationSchema: verifyEmailSchema,
    validateOnMount: true,
  });
  const { receiverValidationEmail } = verifyFormik.values;
  const { resetForm, submitForm } = formik;

  const resetVerify = () => {
    resetVerifyState();
  };

  useEffect(() => {
    setCredentialsRequired(!!password || !!userName);
  }, [password, userName]);

  const toggleSmtpData = () => {
    const newValue = !isSmtpEnabled;
    setSmtpEnabled(newValue);
    if (newValue) {
      resetForm();
    }
  };

  const resetFullForm = () => {
    if (entityExists !== isSmtpEnabled) {
      setSmtpEnabled(entityExists);
    }
    resetForm();
    verifyFormik.resetForm();
    if (!props.smtpSettings?.id) {
      resetVerifyState();
    }
  };

  const verify = async () => {
    await verifyEmailSettings(receiverValidationEmail!, schema.cast(formik.values) as EmailSetting);
  };

  const { dirty, isValid } = formik;

  const toggleMsGraphConfiguration = async () => {
    if (!props.threatDefenceSettings.hasMsGraphSettings) {
      await props.upsertThreatDefenceSettings(props.accountId);
    } else {
      await props.deleteThreatDefenceSettings(props.accountId);
    }
  };

  useEffect(() => {
    getThreatDefenceSettings(props.accountId);
  }, [getThreatDefenceSettings, props.accountId]);

  if (props.isLoading) return <Loader role="progressbar" active />;

  const settingsSection = (
    <section>
      <Typography variant="title2" bold>
        SETTINGS
      </Typography>
      <MsGraphSettings
        checked={props.threatDefenceSettings.hasMsGraphSettings}
        disabled={!canEdit || props.isThreatDefenceSettingsLoading}
        onChange={toggleMsGraphConfiguration}
      />
      {isSmtpDirectSendEnabled && (
        <>
          <div className={styles["smtp-notification-setting"]}>
            <Checkbox
              toggle
              checked={isSmtpEnabled}
              disabled={!canEdit || props.isSmtpLoading}
              label={"SMTP Direct Send"}
              onChange={toggleSmtpData}
              data-testid={"smtp-direct-send"}
              className={styles["checkbox-style"]}
            />
            <Tooltip
              tooltipClassName={styles["system-notification-setting__tooltip_threat-defence"]}
              className={styles["system-notification-setting__tooltip-target"]}
              target={<Icon className="info circle" />}
              content={threatDefenseDirectSendTooltip}
              position="right center"
              hoverable
              hideOnScroll
              showAlways
            />
          </div>

          {isSmtpEnabled && (
            <div className={styles["smtp-form-container"]}>
              <ValidatedForm
                formWidthComputer={16}
                withoutFormik
                dirty={formik.dirty}
                unsavedChangesPrompt={{
                  title: "Exit Without Saving?",
                  message:
                    "Are you sure you want to exit without saving this settings? All information entered will be lost.",
                }}
              >
                <SmtpConfigurationForm
                  formik={formik}
                  canEdit={canEdit}
                  credentialsRequired={credentialsRequired}
                  resetVerify={resetVerify}
                />
                <VerifyEmail
                  disabled={
                    !(
                      (isValid && dirty && verifyFormik.isValid && verifyFormik.dirty) ||
                      (verifyFormik.isValid && verifyFormik.dirty)
                    ) || props.isVerifying
                  }
                  isVerified={props.isVerified}
                  isVerifying={props.isVerifying}
                  receiverValidationEmail={receiverValidationEmail}
                  formik={verifyFormik}
                  verify={verify}
                  errorMessage={props.verificationError}
                  placeholder="Enter Sender Name"
                />
              </ValidatedForm>
            </div>
          )}
        </>
      )}
    </section>
  );

  const domainSections = isThreatDefenceDomainEnabled && (
    <>
      <AssignedDomainSection domainName={props.accountDomain?.domainName} />
      <WhitelistDomainSection domainName={props.accountDomain?.domainName} />
    </>
  );

  const formFooter = canEdit && isSmtpDirectSendEnabled && (
    <FormFooter
      isSaveBtnDisabled={
        !((!isSmtpEnabled && entityExists) || (isValid && dirty)) || (isSmtpEnabled && !props.isVerified)
      }
      isCancelBtnDisabled={(!dirty && isSmtpEnabled === entityExists) || props.isVerifying}
      onSave={submitForm}
      onCancel={resetFullForm}
    />
  );

  const rootContent = isSmtpDirectSendEnabled ? (
    <>
      {settingsSection}
      {domainSections}
    </>
  ) : (
    <>
      {isThreatDefenceDomainEnabled && <AssignedDomainSection domainName={props.accountDomain?.domainName} />}
      {settingsSection}
      {isThreatDefenceDomainEnabled && <WhitelistDomainSection domainName={props.accountDomain?.domainName} />}
    </>
  );

  return (
    <>
      {isSmtpDirectSendEnabled ? (
        <section className="edit-form scrollable-content">
          <div className={styles.root}>{rootContent}</div>
        </section>
      ) : (
        <div className={styles.root}>{rootContent}</div>
      )}
      {formFooter}
    </>
  );
};

const defaultSettings: Partial<EmailSetting> = {
  senderName: "",
  senderEmail: "",
  host: "",
  isSsl: false,
  userName: "",
  password: "",
};

const defaultVerifyEmail: Partial<VerifyEmailModel> = {
  receiverValidationEmail: "",
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const threatDefenceSettings = threatDefenceSettingsSelector(state);
  const smtpSettings = threatDefenceSmtpSettingsSelector(state);
  return {
    accountOnEditPermissions: permissionsToAccountSelector(state),
    threatDefenceSettings: threatDefenceSettings.settings,
    smtpSettings: smtpSettings.settings,
    isSmtpLoading: smtpSettings.isLoading,
    isVerifying: smtpSettings.isVerifying,
    isVerified: smtpSettings.isVerified,
    verificationError: smtpSettings.verificationError,
    smtpError: smtpSettings.error,
    isLoading: threatDefenceSettings.isLoading,
    accountDomain: threatDefenceSettings.accountDomain,
    isThreatDefenceSettingsLoading: threatDefenceSettings.isLoading,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getThreatDefenceSettings: bindAction(fetchThreatDefenceSettings, dispatch),
  upsertThreatDefenceSettings: bindAction(upsertThreatDefenceSettings, dispatch),
  deleteThreatDefenceSettings: bindAction(deleteThreatDefenceSettings, dispatch),
  getSmtpSettings: bindAction(fetchThreatDefenceSmtpSettings, dispatch),
  saveSmtpSettings: bindAction(upsertThreatDefenceSmtpSettings, dispatch),
  deleteSmtpSettings: bindAction(deleteThreatDefenceSmtpSettings, dispatch),
  verifySmtpSettings: bindAction(verifyThreatDefenceSmtpSettings, dispatch),
  resetVerifyState: bindAction(resetVerify, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ThreatDefense);
export default ConnectedComponent;
